import { Box, MenuItem, RadioGroupProps } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { TestRequestAdvancedSearchRQ } from "../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import ProjectDataExportRange from "../../api/models/ProjectDataExportRange";
import WaitingFilter from "../../api/models/WaitingFilter";
import ExportApi from "../../api/services/ExportApi";
import TestRequestApi, { TestRequestCountResponse } from "../../api/services/TestRequestApi";
import AdvancedSearchApi from "../../api/services/AdvancedSearchApi";
import CustomMultipleTextField from "../../components/Input/CustomMultipleTextField";
import StyledAccordion from "../../components/Wrapper/StyledAccordion";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import { useStatusFilter } from "../../contexts/StatusFilterContext";
import OrderedRequestHelper from "../../helper/OrderedRequestHelper";
import {
  getDashboardSearchMode,
  getDashboardUserOrLab,
  getDashboardView,
  getSearchObjectAdvancedDashboard,
  getSearchTermsBasicDashboard,
  getPrioritySearchTermDashboard,
  getShowDashboardKPIs,
  resetSearchObjectAdvancedDashboard,
  resetSearchTermsBasicDashboard,
  setDashboardSearchMode,
  setDashboardView,
  setSearchObjectAdvancedDashboard,
  setSearchTermsBasicDashboard,
  setShowDashboardKPIs,
} from "../../helper/UserSettingsHelper";
import useStyles from "./DashboardPageStyles";
import useEnv from "../../hooks/env";
import useChangingDebounce from "../../hooks/useChangingDebounce.hook";
import ViewMode from "../../models/ViewMode";
import CustomFooterGraphic from "../Requests/CustomFooterGraphic";
import AdvancedSearch from "../Search/AdvancedSearch";
import {
  ADVANCED_SEARCH_INITIAL,
  AdvancedSearchForm,
  formatAdvancedSearchObject,
} from "../Search/AdvancedSearch.utils";
import KpiView from "./Kpi/KpiView";
import StatusFilter, { TestRequestDisplayedStateCount } from "./StatusFilter";
import OverviewColumnsSelector from "./TestRequestsTable/OverviewColumnsSelector";
import { waitingForCustomerDefault } from "./WaitingForCustomerFilter/WaitingForCustomerOptions";
import SearchMode from "../../models/SearchMode";
import { SpecialContentContext } from "../../contexts/SpecialContentContext";
import { DashboardContext } from "../../contexts/DashboardContext";
import ViewControls from "./ViewControls";
import RequestControls from "./RequestControls";
import UserReferenceDefault from "../../api/models/advanced-search/UserReferenceDefault";
import LaboratoryResponse from "../../api/models/LaboratoryResponse";
import AddToFurtherInternalContactDialog from "./AddToFurtherInternalContactDialog/AddToFurtherInternalContactDialog";
import useQueryParams from "../../hooks/useQueryParams.hook";
import DashboardAction, { DashboardActionHandler } from "./DashboardAction";
import { RequestConfig } from "./RequestConfig";
import TestRequestsResultView from "./TestRequestsResultView";
import CustomDropdown from "../../components/Input/CustomDropdown";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import PrioritySearchTerm from "../../api/models/test-request/PrioritySearchTerm";

const ADD_TO_CONTACTS_URL_PARAMETER = "addToContacts";

const DashboardPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const testRequestId = useQueryParams().get(ADD_TO_CONTACTS_URL_PARAMETER);
  const { query: statusFilterQuery } = useStatusFilter();
  const { api } = useEnv();
  const exportApi = new ExportApi(api);
  const testRequestApi = new TestRequestApi(api);
  const advancedSearchApi = new AdvancedSearchApi(api);
  const { updateLabOrUser, labOrUser } = useContext(DashboardContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [prioritySearchTerm, setPrioritySearchTerm] = useState<PrioritySearchTerm>(
    getPrioritySearchTermDashboard() || "",
  );
  const [debouncedPriorityTerm, waitingPriority] = useChangingDebounce(prioritySearchTerm, 1000);
  const [basicSearchTerms, setBasicSearchTerms] = useState<string[]>(getSearchTermsBasicDashboard() || []);
  const [debouncedTerms, waiting] = useChangingDebounce(basicSearchTerms, 1000);
  const [advancedSearch, setAdvancedSearch] = useState<TestRequestAdvancedSearchRQ>(
    getSearchObjectAdvancedDashboard() || ADVANCED_SEARCH_INITIAL,
  );
  const [expandedAdvancedSearch, setExpandedAdvancedSearch] = useState(
    getDashboardSearchMode() === SearchMode.Advanced,
  );

  const [expandedKpiView, setExpandedKpiView] = useState<boolean>(getShowDashboardKPIs());
  const [isLoading, setIsLoading] = useState(false);
  const [orderedRequestHelper] = useState(new OrderedRequestHelper());
  const [statusCount, setStatusCount] = useState<TestRequestDisplayedStateCount[] | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(getDashboardView());
  const [waitingForCustomer, setWaitingForCustomer] = useState<WaitingFilter>(waitingForCustomerDefault);
  const { updateXmasSpecialDialogOpen } = useContext(SpecialContentContext);
  const permissions = useContext(GenericPermissionContext);

  const toggleViewMode = () => {
    if (viewMode === ViewMode.Table) {
      setViewMode(ViewMode.Board);
      setDashboardView(ViewMode.Board);
    } else {
      setViewMode(ViewMode.Table);
      setDashboardView(ViewMode.Table);
    }
  };

  const transformCountResponse = (data: TestRequestCountResponse): TestRequestDisplayedStateCount[] => {
    return data.counts.filter(
      ({ state }) => state !== "DELETED" && state !== "__NONE",
    ) as TestRequestDisplayedStateCount[];
  };

  function refreshCount<T>(fetch: () => Promise<T>): Promise<T> {
    return Promise.all([
      fetch(),
      orderedRequestHelper.ordered(() =>
        expandedAdvancedSearch
          ? advancedSearchApi.countTestRequests(
              (labOrUser as UserReferenceDefault)?.userEmail,
              (labOrUser as LaboratoryResponse)?.key,
              waitingForCustomer,
              advancedSearch,
            )
          : testRequestApi.countTestRequests(
              (labOrUser as UserReferenceDefault)?.userEmail,
              (labOrUser as LaboratoryResponse)?.key,
              debouncedTerms,
              waitingForCustomer,
            ),
      ),
    ]).then(([x, res]) => {
      if (res.data) {
        const displayedStatesCount = transformCountResponse(res.data);
        setStatusCount(displayedStatesCount);
      }
      return x;
    });
  }

  const createNewRequest = (requestConfig: RequestConfig = {}): void => {
    const query = Object.entries(requestConfig).reduce((queryString, [key, value]) => {
      if (typeof value === "boolean" && value) {
        queryString.append(key, "1");
      } else if (typeof value === "string" && value) {
        queryString.append(key, value);
      }
      return queryString;
    }, new URLSearchParams());

    window.localStorage.removeItem("testRequestId");
    history.push(`/requests/new${query.toString() ? `?${query}` : ""}`);
  };

  const exportProjectData = (range: ProjectDataExportRange) => {
    setIsLoading(true);
    return exportApi
      .projectDataExcelExport(range)
      .catch(handleHttpError)
      .finally(() => setIsLoading(false));
  };

  const handleActionsMenuClick: DashboardActionHandler = (action: DashboardAction) => {
    action.type === "CREATE" ? createNewRequest(action.requestConfig) : exportProjectData(action.range);
  };

  const handleWaitingForCustomerChange: RadioGroupProps["onChange"] = (_, value) => {
    setWaitingForCustomer(value as WaitingFilter);
  };

  const handleAdvancedSearchSubmit = (search: TestRequestAdvancedSearchRQ) => {
    const formattedSearchObj = formatAdvancedSearchObject(search);
    setAdvancedSearch(formattedSearchObj);
    setSearchObjectAdvancedDashboard(formattedSearchObj);
  };

  const toggleExpandedAdvancedSearch = () => {
    setExpandedAdvancedSearch(!expandedAdvancedSearch);
    const searchMode = expandedAdvancedSearch ? SearchMode.Basic : SearchMode.Advanced;
    setDashboardSearchMode(searchMode);
  };

  const toggleExpandedKpiView = () => {
    updateXmasSpecialDialogOpen(true);
    setExpandedKpiView(!expandedKpiView);
    setShowDashboardKPIs(!expandedKpiView);
  };

  useEffect(() => {
    const storedLabOrUser = getDashboardUserOrLab();
    updateLabOrUser(storedLabOrUser);
  }, []);

  const BasicSearch = () => (
    <CustomMultipleTextField
      autoFocus
      className={classes.searchBar}
      fullWidth
      clearable
      value={basicSearchTerms}
      onChange={(value) => {
        setBasicSearchTerms(value);
        setSearchTermsBasicDashboard(value);
      }}
      placeholder="Search Requests"
      placeholderIcon={<SearchIcon />}
      resetStoredSearch={resetSearchTermsBasicDashboard}
    />
  );

  const PrioritySearch = () => (
    <CustomDropdown
      name="priority"
      onChange={(e) => {
        setBasicSearchTerms([...basicSearchTerms]);
        setPrioritySearchTerm((e.target as HTMLInputElement).value as PrioritySearchTerm);
      }}
      labelText="Priority"
      value={prioritySearchTerm}
    >
      <MenuItem value="ALL">ALL</MenuItem>
      <MenuItem value="NONE">NONE</MenuItem>
      <MenuItem value="LOW">LOW</MenuItem>
      <MenuItem value="MEDIUM">MEDIUM</MenuItem>
      <MenuItem value="HIGH">HIGH</MenuItem>
    </CustomDropdown>
  );

  const searchTemplate = () => {
    const canViewPriority = !!permissions?.newTestRequest?.description?.viewTestRequestPriority;
    if (canViewPriority) {
      return (
        <Box display="flex" justifyContent="center">
          <Box style={{ marginRight: 16, width: "100%" }}>
            <BasicSearch />
          </Box>
          <Box style={{ width: "30%" }}>
            <PrioritySearch />
          </Box>
        </Box>
      );
    }
    return <BasicSearch />;
  };

  return (
    <div className={classes.root}>
      <h2>DASHBOARD</h2>
      <StyledAccordion expanded={expandedKpiView} toggleExpanded={toggleExpandedKpiView}>
        <KpiView />
      </StyledAccordion>
      <RequestControls
        advancedSearchApi={advancedSearchApi}
        createNewRequest={createNewRequest}
        handleActionsMenuClick={handleActionsMenuClick}
        handleWaitingForCustomerChange={handleWaitingForCustomerChange}
        isLoading={isLoading}
        waitingForCustomer={waitingForCustomer}
      />
      <Box marginTop={3}>
        <StatusFilter statusCount={statusCount} />
      </Box>
      <Box marginTop={3}>
        <StyledAccordion expanded={expandedAdvancedSearch} toggleExpanded={toggleExpandedAdvancedSearch}>
          <AdvancedSearch
            setStoredSearchObject={setSearchObjectAdvancedDashboard}
            resetStoredSearchObject={resetSearchObjectAdvancedDashboard}
            initialValues={advancedSearch as AdvancedSearchForm}
            onSubmit={handleAdvancedSearchSubmit}
          />
        </StyledAccordion>
        <StyledAccordion expanded={!expandedAdvancedSearch} toggleExpanded={toggleExpandedAdvancedSearch}>
          {searchTemplate()}
        </StyledAccordion>
      </Box>
      <Box className={classes.filtersSubContainer}>
        {!viewMode ? <OverviewColumnsSelector /> : <Box style={{ flexGrow: 1 }} />}
        <ViewControls
          expandedAdvancedSearch={expandedAdvancedSearch}
          expandedKpiView={expandedKpiView}
          toggleExpandedAdvancedSearch={toggleExpandedAdvancedSearch}
          toggleExpandedKpiView={toggleExpandedKpiView}
          toggleViewMode={toggleViewMode}
          viewMode={viewMode}
        />
      </Box>
      <Box marginTop={viewMode ? 3 : 2}>
        <TestRequestsResultView
          viewMode={viewMode}
          advancedSearch={expandedAdvancedSearch ? advancedSearch : undefined}
          retriggerSearch={expandedAdvancedSearch}
          statusFilterQuery={statusFilterQuery}
          // eslint-disable-next-line react/jsx-no-bind
          syncWithCount={refreshCount}
          basicSearchTerms={expandedAdvancedSearch ? undefined : debouncedTerms}
          prioritySearchTerm={debouncedPriorityTerm}
          waiting={waiting || waitingPriority}
          waitingForCustomer={waitingForCustomer}
        />
      </Box>
      <CustomFooterGraphic />
      {testRequestId && <AddToFurtherInternalContactDialog testRequestId={testRequestId} />}
    </div>
  );
};

export default DashboardPage;
